import {Component} from '@angular/core';
import {
  FormControl,
  FormGroup,
  Validators,
  ValidatorFn,
  AbstractControl,
  ReactiveFormsModule,
  ValidationErrors
} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {BaseModalComponent} from '../base-modal/base-modal.component';
import {addUserModalName, fieldDisplayNames} from "./configs/add-user-modal-config";
import {MIN_PASSWORD_LENGTH} from '../../../constants/constants';

@Component({
  selector: 'app-add-user-modal',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    ReactiveFormsModule,
    BaseModalComponent
  ],
  templateUrl: './add-user-modal.component.html',
  styleUrls: [
    './add-user-modal.component.scss',
    '../base-modal/base-modal.component.scss'
  ]
})
export class AddUserModalComponent extends BaseModalComponent {
  public userForm: FormGroup = new FormGroup({});
  public passwordVisible: boolean = true;
  public confirmPasswordVisible: boolean = true;
  private readonly minLengthLogin: number = 4;

  public override ngOnInit(): void {
    super.ngOnInit();
    this.modalStatesService.openModal(addUserModalName);
    this.initForm();
    this.onPasswordChanges();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(addUserModalName);
  }

  public async addUser(): Promise<void> {
    const formData = { ...this.userForm.value };
    delete formData.confirmPassword;

    await this.addNewRecord('users', formData)
      .then(() => this.closeDialog('cancel'))
      .catch(() => {
        this.userForm.reset();
        return [];
      });
  }

  public togglePasswordVisibility(): void {
    this.passwordVisible = !this.passwordVisible;
  }

  public toggleConfirmPasswordVisibility(): void {
    this.confirmPasswordVisible = !this.confirmPasswordVisible;
  }

  public getErrorMessage(controlName: string): string | null {
    const control = this.userForm.get(controlName);

    const minLengthMessage = this.getMinLengthError(controlName,
      controlName === 'login' ? this.minLengthLogin : MIN_PASSWORD_LENGTH);

    if (controlName === 'confirmPassword') {
      const mismatchMessage = control?.hasError('mismatch') && control.touched
        ? 'Пароли не совпадают.'
        : null;

      return mismatchMessage || minLengthMessage;
    }

    return control?.hasError('minlength') && control.touched ? minLengthMessage : null;
  }

  private initForm(): void {
    this.userForm = new FormGroup({
      login: new FormControl('', [
        Validators.required,
        Validators.minLength(this.minLengthLogin)
      ]),
      password: new FormControl('', [
        Validators.required,
        Validators.minLength(MIN_PASSWORD_LENGTH)
      ]),
      confirmPassword: new FormControl('', [
        Validators.required,
        Validators.minLength(MIN_PASSWORD_LENGTH)
      ]),
      utmMedium: new FormControl('', Validators.required),
      firstName: new FormControl('', Validators.required),
      lastName: new FormControl('', Validators.required)
    })
  }

  private getMinLengthError(control: string, minLengthField: number): string {
    return `Минимальная длина ${fieldDisplayNames[control]} ${minLengthField} символов.`
  }

  private onPasswordChanges() {
    this.userForm.get('password' as string)?.valueChanges.subscribe(password => {
      const confirmPasswordControl = this.userForm.get('confirmPassword' as string);
      if (password) {
        confirmPasswordControl?.setValidators([
          Validators.required,
          Validators.minLength(MIN_PASSWORD_LENGTH),
          this.matchPassword(password)
        ]);
      } else {
        confirmPasswordControl?.clearValidators();
      }

      confirmPasswordControl?.updateValueAndValidity();
    });
  }

  private matchPassword(password: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return control.value === password ? null : {mismatch: true};
    };
  }
}
